import {faFire} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React from 'react';
import {TwitchClip} from 'react-twitch-embed';
import {Bar, BarChart, Cell, ResponsiveContainer, Tooltip} from 'recharts';
import {CarrotIcon} from './icons/CarrotIcon';
import {ChocolateIcon} from './icons/ChocolateIcon';
import {FootballIcon} from './icons/FootballIcon';
import css from './YearlyReportV2.module.css';
import {PizzaIcon} from './icons/PizzaIcon';
import {RunningMan} from './icons/RunningMan';
import {HikingMan} from './icons/HikingMan';

export const YearlyReportV2 = ({
  report,
  nickname,
  isPublicVersion,
  isScreenshot,
}) => {
  nickname = nickname ? nickname : report.nickname;
  let year = report.year;

  return (
    <div
      className={`${css.root} ${
        isPublicVersion ? css.rootPublic : css.rootDashboard
      }`}
    >
      <main className={css.report}>
        <header className={css.topHeader}>
          <h1>
            {!!nickname ? (
              <>
                <span>{nickname}</span>
                {`'s `}
              </>
            ) : null}
            #{year}OfMyHeart
          </h1>

          <div className={css.headerFootnote}>
            powered by{' '}
            <a href={`https://pulsoid.net/?from=report${year}`} target="_blank">
              PULSOID
            </a>
          </div>
        </header>

        <MainStatisticsSection report={report} />

        <HeartRateHistogramSection report={report} />

        <AchievementsSection report={report} />

        <CaloriesSection report={report} />

        <GamesSection report={report} />

        {!isScreenshot && <ClipsSection report={report} />}

        {!isScreenshot && <ChristmasSongSection report={report} />}
      </main>
    </div>
  );
};

const MainStatisticsSection = ({report}) => {
  let year = report ? report.year : new Date().getFullYear();
  return (
    <section className={css.mainStatistics}>
      <FilledStatisticsBlock
        value={report?.hoursBroadcasted}
        title="Hours Broadcasted"
        icon={
          <img src="https://static.wixstatic.com/media/5986ce_e6cab5ecec914933907a17b8dc09b824~mv2.png/v1/fill/w_174,h_174,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/icons_%D0%9C%D0%BE%D0%BD%D1%82%D0%B0%D0%B6%D0%BD%D0%B0%20%D0%BE%D0%B1%D0%BB%D0%B0%D1%81%D1%82%D1%8C%201%20-%20%D0%BA%D0%BE%D0%BF%D1%96%D1%8F%2012.png" />
        }
      />
      <FilledStatisticsBlock
        value={report?.averageHeartRate}
        title="Average Heart Rate"
        icon={
          <img src="https://static.wixstatic.com/media/5986ce_aa8663e7069648ecb5b98aa0884b8af0~mv2.png/v1/fill/w_174,h_174,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/icons_%D0%9C%D0%BE%D0%BD%D1%82%D0%B0%D0%B6%D0%BD%D0%B0%20%D0%BE%D0%B1%D0%BB%D0%B0%D1%81%D1%82%D1%8C%201%20-%20%D0%BA%D0%BE%D0%BF%D1%96%D1%8F%2013.png" />
        }
      />
      <FilledStatisticsBlock
        value={report?.maximumHeartRate}
        title="Maximum Heart Rate"
        icon={
          <img src="https://static.wixstatic.com/media/5986ce_34998628cd254f4188adf29370e87c6a~mv2.png/v1/fill/w_174,h_174,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/icons_%D0%9C%D0%BE%D0%BD%D1%82%D0%B0%D0%B6%D0%BD%D0%B0%20%D0%BE%D0%B1%D0%BB%D0%B0%D1%81%D1%82%D1%8C%201%20-%20%D0%BA%D0%BE%D0%BF%D1%96%D1%8F%2014.png" />
        }
      />

      <StatisticsBlock
        value={report?.timeStreamedWithHeartRate}
        title="times you streamed with a heart rate widget"
        image="https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/top1.png"
      />
      <StatisticsBlock
        value={`${report?.p95HeartRate} BPM`}
        title="your rampage heart rate"
        image="https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/top2.png"
      />
      <StatisticsBlock
        value={`${report?.rating}%`}
        title={`you are one of the top ${report?.rating}% of Pulsoid active users`}
        image="https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/top3.png"
      />

      {year === 2022 && (
        <StatisticsBlock
          value={report?.strayCompletions}
          title="times you could've completed Stray while using Pulsoid"
        />
      )}

      {year === 2023 && (
        <StatisticsBlock
          value={report?.barbieheimerCompletions}
          title="times you could've watched Barbenheimer back-to-back"
        />
      )}
      {year === 2024 && (
        <StatisticsBlock
          value={Math.floor(report?.hoursBroadcasted / 2.7)}
          title="times you could've watched Dune: Part Two"
        />
      )}

      <StatisticsBlock
        value={report?.heartBeatsCount}
        title="your heartbeats with Pulsoid this year"
      />
      <StatisticsBlock
        value={report?.uniqueWidgetsCount}
        title="unique widgets you have"
      />
    </section>
  );
};

const FilledStatisticsBlock = ({value, title, icon}) => {
  return (
    <div className={css.statisticsFilledBlock}>
      <div className={css.statisticsFilledBlockInfo}>
        {icon}
        <span className={css.statisticsBlockValue}>{value}</span>
      </div>
      <div className={css.statisticsBlockDescription}>{title}</div>
    </div>
  );
};

const StatisticsBlock = ({value, title, image}) => {
  return (
    <div className={css.statisticsBlock}>
      {image && (
        <img src={image} alt={title} className={css.statisticsBlockImage} />
      )}
      <div className={css.statisticsBlockValue}>{value}</div>
      <div className={css.statisticsBlockDescription}>{title}</div>
    </div>
  );
};

const HeartRateHistogramSection = ({report}) => {
  const {histogram = []} = report || {};

  let blueRange = 0;
  let redRange = 0;
  let yellowRange = 0;

  if (histogram?.length > 0) {
    blueRange = Math.floor(histogram[histogram.length - 1].heartRate * 0.3);
    redRange = Math.floor(histogram[histogram.length - 1].heartRate * 0.8);
    yellowRange = Math.floor(histogram[histogram.length - 1].heartRate * 0.5);
  }

  return (
    <section className={css.histogramContainer}>
      <div className={css.sectionHeader}>
        <h2>Heart Rate Histogram</h2>
      </div>

      <ResponsiveContainer minHeight={400} width="99%">
        <BarChart height={400} width={1100} data={histogram} barCategoryGap={0}>
          {/* <XAxis dataKey="heartRate" stroke="#8884d8" /> */}
          <Tooltip
            labelFormatter={() => {
              return '';
            }}
            formatter={(_value, _name, props) => {
              return [
                <div>
                  Heart Rate: {props.payload.heartRate} <br /> Times:{' '}
                  {props.payload.count}
                </div>,
              ];
            }}
          />

          <Bar dataKey="count" isAnimationActive={false}>
            {histogram.map((entry, index) => {
              let barColor = '#04bbaf';

              if (entry.heartRate > redRange) {
                barColor = '#f72d21';
              } else if (entry.heartRate > yellowRange) {
                barColor = '#fdb00a';
              } else if (entry.heartRate < blueRange) {
                barColor = '#14bbaf';
              }

              return (
                <Cell
                  key={`cell-${index}`}
                  fill={barColor}
                  strokeWidth={'1'}
                  stroke="#000000"
                />
              );
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </section>
  );
};

const CaloriesSection = ({report}) => {
  const info = report?.caloriesInfo;
  let year = report ? report.year : new Date().getFullYear();
  return (
    <section>
      <div className={css.sectionHeader}>
        <h2>Calories</h2>
      </div>

      <div className={css.calories}>
        <div className={css.caloriesBlockFirst}>
          <FontAwesomeIcon
            icon={faFire}
            size="6x"
            className={css.caloriesIcon}
          />

          <div className={css.caloriesInfo}>
            <div className={css.caloriesBurned}>{info?.calories}</div>
            <div className={css.caloriesDescription}>
              Calories you burned while using Pulsoid in {year}
            </div>
          </div>
        </div>

        <div className={css.caloriesBlockSecond}>
          <div className={css.caloriesExampleTitle}>Which is the same as:</div>
          {year === 2022 && (
            <div className={css.caloriesExampleContent}>
              <div className={css.caloriesExampleItem}>
                <span className={css.caloriesExampleItemIcon}>
                  <FootballIcon />
                </span>
                burned during {info?.footballGames} football games
              </div>
              <div className={css.caloriesExampleItem}>
                <span className={css.caloriesExampleItemIcon}>
                  <CarrotIcon />
                </span>
                {info?.carrots} medium carrots
              </div>
              <div className={css.caloriesExampleItem}>
                <span className={css.caloriesExampleItemIcon}>
                  <ChocolateIcon />
                </span>
                {info?.snickers} Snickers bars
              </div>
            </div>
          )}
          {(year === 2023 || year === 2024) && (
            <div className={css.caloriesExampleContent}>
              <div className={css.caloriesExampleItem}>
                <span className={css.caloriesExampleItemIcon}>
                  <RunningMan />
                </span>
                running nearly {info?.marathons} marathons
              </div>
              <div className={css.caloriesExampleItem}>
                <span className={css.caloriesExampleItemIcon}>
                  <PizzaIcon />
                </span>
                about {info?.pizzas} large pepperoni pizzas
              </div>
              <div className={css.caloriesExampleItem}>
                <span className={css.caloriesExampleItemIcon}>
                  <HikingMan />
                </span>
                hiking for about {info?.hiking} hours
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const GamesSection = ({report}) => {
  let games = report?.gamesStatistics;
  games = games?.filter((game) => game.categoryId != '');
  let year = report ? report.year : new Date().getFullYear();

  return !!games?.length ? (
    <section>
      <div className={css.sectionHeader}>
        <h2>Most Played Games</h2>
        <h5>game-related stats were collected since Jan {year}</h5>
      </div>

      <div className={css.gamesContainer}>
        <Game gameStatistics={games[0]} />

        {games.length === 2 && (
          <Game gameStatistics={games[1]} isSmall={true} />
        )}

        {games.length === 3 && (
          <div className={css.secondaryGamesContainer}>
            <Game gameStatistics={games[1]} isSmall={true} />
            <Game gameStatistics={games[2]} isSmall={true} />
          </div>
        )}
      </div>
    </section>
  ) : null;
};

const Game = ({gameStatistics, isSmall = false}) => {
  const {boxArtUrl, calories, hoursBroadcasted, maximumHeartRate} =
    gameStatistics;

  const rootClassName = css.game + (isSmall ? ' ' + css.gameSmall : '');

  return (
    <div className={rootClassName}>
      <img src={boxArtUrl} alt="game cover" className={css.gameCover} />
      <div className={css.gameInfo}>
        <div className={css.gameInfoItem}>
          <div className={css.gameInfoLabel}>Max HR</div>
          <div
            className={`${css.gameInfoValue} ${css.gameInfoValue_heartRate}`}
          >
            {maximumHeartRate}
          </div>
        </div>
        <div className={css.gameInfoItem}>
          <div className={css.gameInfoLabel}>Hours</div>
          <div className={`${css.gameInfoValue} ${css.gameInfoValue_hours}`}>
            {hoursBroadcasted}
          </div>
        </div>
        <div className={css.gameInfoItem}>
          <div className={css.gameInfoLabel}>Calories</div>
          <div className={css.gameInfoValue}>{calories}</div>
        </div>
      </div>
    </div>
  );
};

export const ClipsSection = ({report}) => {
  if (!report?.clips?.length) {
    return null;
  }

  return (
    <section>
      <div className={css.sectionHeader}>
        <h2>Most Watched Clips</h2>
        <h5>Clips created automatically by Pulsoid</h5>
      </div>

      <div className={css.clipsList}>
        {report?.clips.map((clip) => (
          <div className={css.clip} key={clip.id}>
            <TwitchClip
              clip={clip.clipUrl.split('/').pop()}
              className={css.clipIframe}
              allowFullScreen
              height=""
              width=""
              autoplay={false}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export const ChristmasSongSection = ({report}) => {
  return (
    <section>
      <div className={css.sectionHeader}>
        <h2>Christmas song for you</h2>
        <h5>with the closest BPM to your rampage heart rate</h5>
      </div>

      <div className={css.songContainer}>
        <iframe
          src={`https://open.spotify.com/embed/track/${report?.songId}?utm_source=generator&theme=0`}
          width="100%"
          height="80"
          frameBorder="0"
          allowFullScreen=""
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        />
      </div>
    </section>
  );
};

const achievementToPropsMap = {
  lifetime_supporter: {
    title: 'BROther in arms',
    description: 'Lifetime supporter 💛',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/lifetime_supported.png',
  },

  subscribed_supporter: {
    title: 'BROther in arms',
    description: 'Supporting Pulsoid for {0} days',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/subscribed_supporter.png',
  },

  referred_users: {
    title: 'Cupid',
    description: 'Referred {0} users',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/referred_users.png',
  },

  played_games: {
    title: 'Jack of all games',
    description: 'played {0} games with Pulsoid',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/played_games.png',
  },

  active_community_members: {
    title: 'Heart of gold',
    description: 'Active community member',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/active_community_members.png',
  },

  integrations_user: {
    title: 'Scripting a bit',
    description: 'Used advanced integrations',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/integrations_user.png',
  },

  reports_user: {
    title: 'Finger on the pulse',
    description: 'Visited Highlights for {0} times',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/reports_user.png',
  },

  editor_for_hire: {
    title: 'Editor for hire',
    description: '{0} clips were created by automated clips feature',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/editor_for_hire.png',
  },

  widgets_wizard: {
    title: 'Widgets wizard',
    description: 'Added {0} widgets and edited {1} times',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/widgets_wizard.png',
  },

  widgets_explorer: {
    title: 'Widgets explorer',
    description: 'Added {0} widgets and edited {1} times',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/widgets_explorer.png',
  },

  times_streamed_young: {
    title: 'Young at heart',
    description: 'Streamed {0} times',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/times_streamed_young.png',
  },

  times_streamed_commited: {
    title: 'Commited',
    description: 'Streamed {0} times',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/times_streamed_commited.png',
  },

  times_streamed_soulmate: {
    title: 'Soulmate',
    description: 'Streamed {0} times',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/times_streamed_soulmate.png',
  },

  heart_rate_intensity_easy: {
    title: 'Easy mode',
    description: 'Average heart rate is {0}% of personal max',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/heart_rate_intensity_easy.png',
  },

  heart_rate_intensity_light: {
    title: 'Light mode',
    description: 'Average heart rate is {0}% of personal max',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/heart_rate_intensity_light.png',
  },

  heart_rate_intensity_moderate: {
    title: 'Moderate mode',
    description: 'Average heart rate is {0}% of personal max',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/heart_rate_intensity_moderate.png',
  },

  heart_rate_intensity_hard: {
    title: 'Hard mode',
    description: 'Average heart rate is {0}% of personal max',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/heart_rate_intensity_hard.png',
  },

  heart_rate_intensity_vigorous: {
    title: 'Vigorous mode',
    description: 'Average heart rate is {0}% of personal max',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/heart_rate_intensity_vigorous.png',
  },
  fear_seeker: {
    title: 'Fear seeker',
    description: 'Streamed horror games this Spooktober!',
    image:
      'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content/yearly-report-2022/achievements/fear_seeker.png',
  },
};

const AchievementsSection = ({report}) => {
  const MAX_ACHIEVEMENTS = 6;
  let achievements = (report?.achievements || []).slice(0, MAX_ACHIEVEMENTS);

  let achievementsContainerClass = css.achievementsContainer;
  if (achievements?.length === 1) {
    achievementsContainerClass += ` ${css.achievementsContainerSingle}`;
  } else if (achievements?.length === 2 || achievements?.length === 4) {
    achievementsContainerClass += ` ${css.achievementsContainerDouble}`;
  } else if (achievements?.length === 5) {
    achievementsContainerClass += ` ${css.achievementsContainerFive}`;
  }

  return !!achievements?.length ? (
    <section>
      <div className={css.sectionHeader}>
        <h2>Achievements</h2>
      </div>

      <div className={achievementsContainerClass}>
        {achievements.map((achievement) => {
          const achievemtProps = achievementToPropsMap[achievement.name];

          if (achievement === null) {
            return <div />;
          }

          if (!achievemtProps) {
            return null;
          }

          const description = achievemtProps.description
            .replace('{0}', achievement?.value1)
            .replace('{1}', achievement?.value2);

          return (
            <div className={css.achievement} key={achievement.name}>
              <img
                className={css.achievementImage}
                src={achievemtProps.image}
              />
              <div className={css.achievementTitle}>{achievemtProps.title}</div>
              <div className={css.achievementDescription}>{description}</div>
            </div>
          );
        })}
      </div>
    </section>
  ) : null;
};
